import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { 
  Edit, 
  Settings, 
  MapPin, 
  Shield, 
  Bell, 
  CreditCard,
  LogOut,
  Camera,
  Globe,
  Briefcase
} from 'lucide-react';

const Profile = ({ user, setUser }) => {
  const { logout } = useAuth();
  const { t, language, changeLanguage } = useLanguage();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: user.name,
    status: user.status,
    skills: user.skills.join(', ')
  });

  const handleSave = () => {
    setUser({
      ...user,
      name: formData.name,
      status: formData.status,
      skills: formData.skills.split(',').map(s => s.trim()).filter(s => s)
    });
    setIsEditing(false);
  };

  const handleLanguageChange = (newLanguage) => {
    changeLanguage(newLanguage);
  };

  const profileSections = [
    {
      title: t('locationPrivacy'),
      icon: Shield,
      items: [
        { label: t('locationSharing'), value: t('visibleToNearby'), action: true },
        { label: t('discoveryRadius'), value: '5 km', action: true },
        { label: t('onlineStatus'), value: t('onlineStatus'), action: true }
      ]
    },
    {
      title: t('notifications'),
      icon: Bell,
      items: [
        { label: t('newMessages'), value: t('enabled'), action: true },
        { label: t('eventInvitations'), value: t('enabled'), action: true },
        { label: t('nearbyPings'), value: t('enabled'), action: true }
      ]
    },
    {
      title: t('account'),
      icon: Settings,
      items: [
        { label: t('subscription'), value: t('freePlan'), action: true },
        { label: t('language'), value: language === 'en' ? 'English' : 'Türkçe', action: true, onClick: () => handleLanguageChange(language === 'en' ? 'tr' : 'en') },
        { label: t('dataExport'), value: '', action: true },
        { label: t('deleteAccount'), value: '', action: true, danger: true }
      ]
    }
  ];

  return (
    <div className="screen" style={{ paddingBottom: '0' }}>
      {/* Profile Header */}
      <div className="profile-header">
        <div style={{ position: 'relative', display: 'inline-block' }}>
          <img 
            src={user.avatar} 
            alt={user.name}
            className="profile-avatar-large"
          />
          <button style={{
            position: 'absolute',
            bottom: '4px',
            right: '4px',
            width: '32px',
            height: '32px',
            background: 'rgba(255, 255, 255, 0.9)',
            border: '2px solid white',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: '#667eea'
          }}>
            <Camera size={14} />
          </button>
        </div>
        
        {isEditing ? (
          <div style={{ width: '100%', maxWidth: '280px' }}>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              style={{
                background: 'rgba(255, 255, 255, 0.2)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '8px',
                padding: '8px 12px',
                color: 'white',
                fontSize: '20px',
                fontWeight: '700',
                textAlign: 'center',
                width: '100%',
                marginBottom: '8px'
              }}
            />
            <input
              type="text"
              value={formData.status}
              onChange={(e) => setFormData({...formData, status: e.target.value})}
              style={{
                background: 'rgba(255, 255, 255, 0.2)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '8px',
                padding: '6px 12px',
                color: 'white',
                fontSize: '14px',
                textAlign: 'center',
                width: '100%',
                marginBottom: '12px'
              }}
            />
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
              <button
                onClick={handleSave}
                style={{
                  background: 'rgba(255, 255, 255, 0.9)',
                  color: '#667eea',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '8px 16px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                {t('save')}
              </button>
              <button
                onClick={() => setIsEditing(false)}
                style={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  borderRadius: '6px',
                  padding: '8px 16px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                {t('cancel')}
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="profile-name">{user.name}</div>
            <div className="profile-status-large">{user.status}</div>
            <button
              onClick={() => setIsEditing(true)}
              style={{
                background: 'rgba(255, 255, 255, 0.2)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '6px',
                color: 'white',
                padding: '8px 16px',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer',
                marginTop: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              <Edit size={14} />
              {t('editProfile')}
            </button>
          </>
        )}
      </div>

      <div className="profile-content">
        {/* Skills Section */}
        <div className="profile-section">
          <div className="section-title">{t('skillsExpertise')}</div>
          {isEditing ? (
            <input
              type="text"
              value={formData.skills}
              onChange={(e) => setFormData({...formData, skills: e.target.value})}
              placeholder="e.g. React, Node.js, Design"
              style={{
                background: 'rgba(0, 0, 0, 0.02)',
                border: '1px solid rgba(0, 0, 0, 0.1)',
                borderRadius: '8px',
                padding: '10px 12px',
                fontSize: '14px',
                width: '100%'
              }}
            />
          ) : (
            <div className="profile-skills">
              {user.skills.map((skill, index) => (
                <span key={index} className="skill-tag-large">{skill}</span>
              ))}
            </div>
          )}
        </div>

        {/* Quick Stats */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr',
          gap: '12px',
          marginBottom: '20px'
        }}>
          <div style={{
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: '12px',
            padding: '16px',
            textAlign: 'center',
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)'
          }}>
            <div style={{ fontSize: '20px', fontWeight: '700', color: '#667eea' }}>12</div>
            <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>{t('events')}</div>
          </div>
          <div style={{
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: '12px',
            padding: '16px',
            textAlign: 'center',
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)'
          }}>
            <div style={{ fontSize: '20px', fontWeight: '700', color: '#667eea' }}>28</div>
            <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>Connections</div>
          </div>
          <div style={{
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: '12px',
            padding: '16px',
            textAlign: 'center',
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)'
          }}>
            <div style={{ fontSize: '20px', fontWeight: '700', color: '#667eea' }}>5</div>
            <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>Cities</div>
          </div>
        </div>

        {/* Settings Sections */}
        {profileSections.map((section, index) => (
          <div key={index} className="profile-section">
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '16px'
            }}>
              <section.icon size={18} style={{ color: '#667eea' }} />
              <div className="section-title">{section.title}</div>
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {section.items.map((item, itemIndex) => (
                <div 
                  key={itemIndex} 
                  onClick={item.onClick}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '8px 0',
                    borderBottom: itemIndex < section.items.length - 1 ? '1px solid rgba(0, 0, 0, 0.05)' : 'none',
                    cursor: item.onClick ? 'pointer' : 'default'
                  }}
                >
                  <div>
                    <div style={{
                      fontSize: '14px',
                      fontWeight: '500',
                      color: item.danger ? '#ef4444' : '#1a1a1a'
                    }}>
                      {item.label}
                    </div>
                    {item.value && (
                      <div style={{
                        fontSize: '12px',
                        color: '#666',
                        marginTop: '2px'
                      }}>
                        {item.value}
                      </div>
                    )}
                  </div>
                  {item.action && (
                    <div style={{
                      width: '6px',
                      height: '6px',
                      background: '#ccc',
                      borderRadius: '50%'
                    }} />
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* Sign Out */}
        <button 
          onClick={logout}
          style={{
            width: '100%',
            background: 'rgba(239, 68, 68, 0.1)',
            color: '#ef4444',
            border: '1px solid rgba(239, 68, 68, 0.2)',
            borderRadius: '12px',
            padding: '16px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            marginBottom: '100px'
          }}
        >
          <LogOut size={16} />
          {t('signOut')}
        </button>
      </div>
    </div>
  );
};

export default Profile;